import { CircularProgress, Container, makeStyles } from '@material-ui/core';
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useHistory } from 'react-router';
import { useUser } from '../../contexts';
import api, { isApiError, ApiError } from '../../api/api';
import { InvestorProfile, FileUploadResult, ImageProvider, InvestorUpdateBody, ErrorCode } from '../../types/types';
import InvestorProfileHolder from './InvestorProfileHolder';
import { useAlert } from '../../contexts/AlertContext';
import InvestorTopbar from './InvestorTopbar';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    minWidth: theme.breakpoints.values.md,
    width: '100%',
    backgroundColor: theme.palette.background.default
  },
  container: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1
  },
  paper: {
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2)
    },
    padding: theme.spacing(4)
  },
  topSection: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap'
  }
}));

interface InvestorProfileParamTypes {
  profileId: string
}

const InvestorProfilePage = () => {
  const classes = useStyles();
  const { getAccessToken, userInfo, saveProfileImage } = useUser();
  const [loading, setLoading] = useState<boolean>(true);
  const [profile, setProfile] = useState<InvestorProfile>();
  const { profileId } = useParams<InvestorProfileParamTypes>();
  const { postNewAlert } = useAlert();
  const history = useHistory();

  useEffect(() => {
    const fetchInvestorProfile = async (id: number) => {
      try {
        const accessToken = await getAccessToken();
        const profileRes = await api.getInvestorProfile(accessToken, id);
        setProfile(profileRes as InvestorProfile);
      } catch (e: unknown) {
        console.error('An error occurred fetching the investor profile: ', e);
        postNewAlert('An error occurred fetching the investor profile.');
        if (isApiError(e) && (e as ApiError).code === ErrorCode.NoReadAccess) {
          history.replace('/');
        }
      }
      setLoading(false);
    };
    fetchInvestorProfile(parseInt(profileId));

    // another case where access token as a dependency would only ruin everything
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  const profileImageProvider: ImageProvider = useMemo(() => {
    if (profile && profile.editable) {
      const updateProfileImage = async (image: FileUploadResult) => {
        const newUrl = await saveProfileImage(image, parseInt(profileId));
        if (newUrl && profile) {
          setProfile({
            ...profile,
            profileImage: newUrl
          });
        }
      };
      return ({
        imgUrl: profile?.profileImage || '',
        tempImgUrl: null,
        updateImage: updateProfileImage,
        editable: true
      });
    } else {
      return {
        imgUrl: profile?.profileImage || '',
        tempImgUrl: null,
        updateImage: undefined,
        editable: false
      };
    }
  }, [profile, profileId, saveProfileImage]);


  if (loading || !userInfo) return <CircularProgress />;
  const { role } = userInfo;

  const handleUpdate = async (payload: InvestorUpdateBody) => {
    const accessToken = await getAccessToken();
    try {
      await api.updateInvestorProfile(accessToken, parseInt(profileId), payload);
    } catch (e: unknown) {
      console.error('An error occurred updating investor profile: ', e);
      postNewAlert('An error occurred updating the investor profile.');
    }
    try {
      const newProfile = await api.getInvestorProfile(accessToken, parseInt(profileId));
      setProfile(newProfile);
    } catch (e: unknown) {
      console.error('An error occurred fetching the investor profile: ', e);
      postNewAlert('An error occurred fetching the investor profile.');
      if (isApiError(e) && (e as ApiError).code === ErrorCode.NoReadAccess) {
        history.replace('/');
      }
    }
  };

  const topbarFactory = () => (
    profile && <InvestorTopbar profile={profile} role={role} />
  );


  return (
    <Container className={classes.root} maxWidth='xl'>
      <InvestorProfileHolder
        profile={profile}
        profileImageProvider={profileImageProvider}
        handleUpdate={((profile && profile.editable) && handleUpdate) || undefined}
        renderTopbar={topbarFactory}
      />
    </Container>
  );
};

export default InvestorProfilePage;
